import axios from "axios";
import {
  getAuthHeaders,
  getIdToken,
  isAccountSwitchPending,
  clearProductCsrfToken,
  ensureFreshIdToken,
} from "@xinosolutions/xs-auth-react";
import { baseUrl } from "../config/config";
import { createLogger } from "../utils/logger";
import { createCsrfTokenManager } from "../utils/csrfTokenManager";
import {
  buildInvokeRequestConfig,
  getErrorResponseMessage,
  isAxiosCancelError,
  isCanceledResponse,
  isCsrfForbiddenError,
} from "../utils/invokeApiUtils";

const log = createLogger("invokeApi");

axios.defaults.headers.post["Content-Type"] = "application/json";

const APP_CONTEXT = "admin";

const csrf = createCsrfTokenManager({
  axios,
  baseUrl,
  appContext: APP_CONTEXT,
});

export const clearCsrfToken = csrf.clearCsrfToken;

function isAuthAttemptRequest(url) {
  if (typeof url !== "string") return false;
  return (
    url.includes("/api/member_session") ||
    url.includes("/api/member_init") ||
    url.includes("/api/admin_users/member_init")
  );
}

axios.interceptors.request.use(async (config) => {
  if (!config.skip401Refresh && !isAuthAttemptRequest(config.url)) {
    await ensureFreshIdToken();
  }
  const auth = getAuthHeaders();
  if (auth.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = auth.Authorization;
  }
  return csrf.requestInterceptor(config);
});

/** @param {import("axios").AxiosError} error */
export function isRequestCanceled(error) {
  return isAxiosCancelError(error, axios);
}

export { isCanceledResponse };

axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error.response?.status;
    const original = error.config;
    const data = error.response?.data;
    const msg = getErrorResponseMessage(data);

    if (
      isCsrfForbiddenError(status, msg) &&
      original &&
      !original.__csrfRetry
    ) {
      clearCsrfToken();
      original.__csrfRetry = true;
      return axios(original);
    }

    if (
      status === 401 &&
      original &&
      !original.skip401Refresh &&
      !original.__authRetry &&
      !isAuthAttemptRequest(original.url)
    ) {
      clearCsrfToken();
      clearProductCsrfToken();
      original.__authRetry = true;
      const token = await ensureFreshIdToken({ force: true });
      if (token) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${token}`;
        return axios(original);
      }
      if (!getIdToken() && !isAccountSwitchPending() && typeof window !== "undefined") {
        window.dispatchEvent(new Event("user-logout"));
      }
    }

    return Promise.reject(error);
  }
);

export async function invokeApi({
  path,
  method = "GET",
  headers = {},
  queryParams = {},
  postData = {},
  skip401Handler = false,
  signal = null,
}) {
  const reqObj = buildInvokeRequestConfig({
    baseUrl,
    path,
    method,
    headers,
    queryParams,
    postData,
    skip401Handler,
    signal,
    appContext: APP_CONTEXT,
  });

  try {
    const results = await axios(reqObj);
    return results.data;
  } catch (error) {
    if (isAxiosCancelError(error, axios)) {
      return { canceled: true };
    }
    const status = error.response?.status;
    const data = error.response?.data;
    let message = getErrorResponseMessage(data);
    if (!message && status === 413) {
      message =
        "The request is too large. Reduce the amount of data or file size and try again.";
    }

    log.error("invokeApi error", {
      path,
      method,
      status: error.response?.status,
      message: message || error.message,
    });
    return {
      code: status,
      message,
    };
  }
}
